import { all, call, put, takeLatest } from "redux-saga/effects";
import adminApi, { clearToken, getToken, setToken } from "../../services/adminApi";
import actions from "./actions";

const errorMessage = (error) => error?.message || String(error);

function* checkAuth() {
  if (!getToken()) {
    yield put({ type: actions.CHECK_AUTH_FAILED });
    return;
  }
  try {
    const { user } = yield call(adminApi.me);
    if (!user.roles.includes("admin")) throw new Error("ADMIN_REQUIRED");
    yield put({ type: actions.CHECK_AUTH_SUCCESS, user });
  } catch (error) {
    clearToken();
    yield put({ type: actions.CHECK_AUTH_FAILED, error: errorMessage(error) });
  }
}

export function* signIn(action) {
  try {
    const { token } = yield call(adminApi.signIn, action.email, action.password);
    setToken(token);
    const { user } = yield call(adminApi.me);
    if (!user.roles.includes("admin")) throw new Error("ADMIN_REQUIRED");
    yield put({ type: actions.SIGN_IN_SUCCESS, user });
  } catch (error) {
    clearToken();
    yield put({ type: actions.SIGN_IN_FAILED, error: errorMessage(error) });
  }
}

function* signOut() {
  try {
    yield call(adminApi.signOut);
  } finally {
    clearToken();
    yield put({ type: actions.SIGN_OUT_SUCCESS });
  }
}

function* fetchDashboard() {
  try {
    const summary = yield call(adminApi.analytics);
    yield put({ type: actions.FETCH_DASHBOARD_SUCCESS, summary });
  } catch (error) {
    yield put({ type: actions.FETCH_DASHBOARD_FAILED, error: errorMessage(error) });
  }
}

function* fetchUsers(action) {
  try {
    const data = yield call(adminApi.users, action.params);
    yield put({ type: actions.FETCH_USERS_SUCCESS, data });
  } catch (error) {
    yield put({ type: actions.FETCH_USERS_FAILED, error: errorMessage(error) });
  }
}

function* fetchUser(action) {
  try {
    const detail = yield call(adminApi.user, action.id);
    yield put({ type: actions.FETCH_USER_SUCCESS, detail });
  } catch (error) {
    yield put({ type: actions.FETCH_USER_FAILED, error: errorMessage(error) });
  }
}

function* updateUser(action) {
  try {
    yield call(adminApi.updateUser, action.id, action.user);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: action.feedback });
    yield put(actions.fetchUsers(action.params));
    if (action.feedback?.refreshDetail) yield put(actions.fetchUser(action.id));
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

function* extendTrial(action) {
  try {
    yield call(adminApi.extendTrial, action.id, action.days, action.reason);
    yield put({type: actions.MUTATION_SUCCESS, feedback: {type: "success", operation: "trialExtended", messageKey: "admin.users.trialExtended"}});
    yield put(actions.fetchUsers(action.params));
    yield put(actions.fetchUser(action.id));
  } catch (error) {
    yield put({type: actions.MUTATION_FAILED, error: errorMessage(error)});
  }
}

// Scoped to the account rather than an organisation, so it also reaches
// accounts that belong to none.
export function* deleteUser(action) {
  try {
    yield call(adminApi.deleteUser, action.id);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: {
      type: "success",
      operation: "accountDeleted",
      messageKey: "admin.users.accountDeleted",
      values: { name: action.displayName },
    } });
    yield put(actions.clearUserDetail());
    yield put(actions.fetchUsers(action.params));
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

function* fetchOrganizations(action) {
  try {
    const data = yield call(adminApi.organizations, action.params);
    yield put({ type: actions.FETCH_ORGANIZATIONS_SUCCESS, data });
  } catch (error) {
    yield put({ type: actions.FETCH_ORGANIZATIONS_FAILED, error: errorMessage(error) });
  }
}

function* fetchOrganization(action) {
  try {
    const detail = yield call(adminApi.organization, action.id);
    yield put({ type: actions.FETCH_ORGANIZATION_SUCCESS, detail });
  } catch (error) {
    yield put({ type: actions.FETCH_ORGANIZATION_FAILED, error: errorMessage(error) });
  }
}

export function* createOrganization(action) {
  try {
    yield call(adminApi.createOrganization, action.organization);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: { type: "success", operation: "organizationCreated", messageKey: "admin.organizations.created" } });
    yield put(actions.fetchOrganizations());
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

function* updateOrganization(action) {
  try {
    yield call(adminApi.updateOrganization, action.id, action.organization);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: {
      type: "success",
      operation: action.organization.billing_status ? "billingChanged" : "organizationUpdated",
      messageKey: action.organization.billing_status ? "admin.organizations.billingChanged" : "admin.organizations.updated",
      values: { name: action.displayName || action.organization.name },
      translatedValues: action.organization.billing_status
        ? { status: `admin.common.statuses.${action.organization.billing_status}` }
        : {},
    } });
    yield put(actions.fetchOrganizations());
    yield put(actions.fetchOrganization(action.id));
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

function* inviteOrganization(action) {
  try {
    yield call(adminApi.inviteToOrganization, action.id, action.invite);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: {
      type: "success", operation: "invitationSent", messageKey: "admin.organizations.invitationSent", values: { email: action.invite.email },
    } });
    yield put(actions.fetchOrganizations());
    if (action.source === "detail") yield put(actions.fetchOrganization(action.id));
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

function* resendInvitation(action) {
  try {
    yield call(adminApi.resendOrganizationInvite, action.organizationId, action.inviteId);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: {
      type: "success", operation: "invitationResent", messageKey: "admin.users.invitationResent", values: { email: action.email },
    } });
    if (action.source === "detail") yield put(actions.fetchOrganization(action.organizationId));
    else yield put(actions.fetchUsers());
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

// The account is gone for good, so the organisation is refetched rather
// than patched in place: seat counts and the invitation list both move.
export function* deleteMember(action) {
  try {
    yield call(adminApi.deleteOrganizationMember, action.organizationId, action.userId);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: {
      type: "success",
      operation: "memberDeleted",
      messageKey: "admin.organizationDetails.memberDeleted",
      values: { name: action.displayName },
    } });
    yield put(actions.fetchOrganization(action.organizationId));
    yield put(actions.fetchOrganizations());
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

function* issueInvoice(action) {
  try {
    yield call(adminApi.issueInvoice, action.id, action.invoice);
    yield put({ type: actions.MUTATION_SUCCESS, feedback: {
      type: "success", operation: "invoiceSent", messageKey: "admin.organizations.invoiceSent",
    } });
    yield put(actions.fetchOrganizations());
  } catch (error) {
    yield put({ type: actions.MUTATION_FAILED, error: errorMessage(error) });
  }
}

function* fetchIntelligenceCosts(action) {
  try {
    const report = yield call(adminApi.intelligenceModelCosts, action.params);
    yield put({ type: actions.FETCH_INTELLIGENCE_COSTS_SUCCESS, report });
  } catch (error) {
    yield put({ type: actions.FETCH_INTELLIGENCE_COSTS_FAILED, error: errorMessage(error) });
  }
}

function* actionWatcher() {
  yield takeLatest(actions.CHECK_AUTH_REQUEST, checkAuth);
  yield takeLatest(actions.SIGN_IN_REQUEST, signIn);
  yield takeLatest(actions.SIGN_OUT_REQUEST, signOut);
  yield takeLatest(actions.FETCH_DASHBOARD_REQUEST, fetchDashboard);
  yield takeLatest(actions.FETCH_USERS_REQUEST, fetchUsers);
  yield takeLatest(actions.FETCH_USER_REQUEST, fetchUser);
  yield takeLatest(actions.UPDATE_USER_REQUEST, updateUser);
  yield takeLatest(actions.EXTEND_TRIAL_REQUEST, extendTrial);
  yield takeLatest(actions.DELETE_USER_REQUEST, deleteUser);
  yield takeLatest(actions.FETCH_ORGANIZATIONS_REQUEST, fetchOrganizations);
  yield takeLatest(actions.FETCH_ORGANIZATION_REQUEST, fetchOrganization);
  yield takeLatest(actions.CREATE_ORGANIZATION_REQUEST, createOrganization);
  yield takeLatest(actions.UPDATE_ORGANIZATION_REQUEST, updateOrganization);
  yield takeLatest(actions.INVITE_ORGANIZATION_REQUEST, inviteOrganization);
  yield takeLatest(actions.RESEND_INVITATION_REQUEST, resendInvitation);
  yield takeLatest(actions.DELETE_MEMBER_REQUEST, deleteMember);
  yield takeLatest(actions.ISSUE_INVOICE_REQUEST, issueInvoice);
  yield takeLatest(actions.FETCH_INTELLIGENCE_COSTS_REQUEST, fetchIntelligenceCosts);
}

export default function* adminSagas() {
  yield all([actionWatcher()]);
}
